// Wishlist functionality
class Wishlist {
  constructor() {
    this.storageKey = 'shopify_wishlist';
    this.ratioKey = 'sg_dealer_price_ratio';
    this.renderToken = 0;
    this.live = {}; // handle -> live price data from Shopify
    this.init();
  }

  init() {
    this.updateHeaderCount();
    this.bindEvents();
    this.startPassiveLearning();

    // If on wishlist page, load items
    if (window.location.pathname.includes('/pages/wishlist')) {
      this.loadWishlistPage();
    }
  }

  /* ---------- storage ---------- */

  getWishlist() {
    try {
      const wishlist = localStorage.getItem(this.storageKey);
      return wishlist ? JSON.parse(wishlist) : [];
    } catch (e) {
      return [];
    }
  }

  saveWishlist(wishlist) {
    localStorage.setItem(this.storageKey, JSON.stringify(wishlist));
    this.updateHeaderCount();
  }

  addToWishlist(productData) {
    const wishlist = this.getWishlist();
    const exists = wishlist.find(item => item.handle === productData.handle);
    if (!exists) {
      wishlist.push(productData);
      this.saveWishlist(wishlist);
      return true;
    }
    return false;
  }

  removeFromWishlist(handle) {
    this.saveWishlist(this.getWishlist().filter(item => item.handle !== handle));
  }

  isInWishlist(handle) {
    return this.getWishlist().some(item => item.handle === handle);
  }

  updateHeaderCount() {
    const count = this.getWishlist().length;
    document.querySelectorAll('[data-wishlist-count]').forEach(el => {
      el.textContent = count;
      el.style.display = count === 0 ? 'none' : 'flex';
    });
  }

  /* ---------- events ---------- */

  bindEvents() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.wishlist-btn');
      if (btn) {
        e.preventDefault();
        e.stopPropagation();
        this.toggleWishlist(btn);
      }

      const removeBtn = e.target.closest('.wishlist-remove-btn');
      if (removeBtn) {
        this.removeFromWishlist(removeBtn.dataset.handle);
        this.loadWishlistPage();
      }

      const clearBtn = e.target.closest('#clear-wishlist');
      if (clearBtn) {
        if (confirm('Are you sure you want to clear your wishlist?')) {
          localStorage.removeItem(this.storageKey);
          this.loadWishlistPage();
          this.updateHeaderCount();
        }
      }
    });

    this.updateWishlistButtons();
  }

  toggleWishlist(btn) {
    const handle = btn.dataset.productHandle;

    if (this.isInWishlist(handle)) {
      this.removeFromWishlist(handle);
      btn.classList.remove('active');
    } else {
      fetch(this.productUrl(handle))
        .then(response => response.json())
        .then(product => {
          const info = this.getPriceData(product);
          this.addToWishlist({
            id: product.id,
            handle: product.handle,
            title: product.title,
            price: info.price,
            compare_price: info.compare,
            variantId: info.variantId,
            image: product.featured_image,
            url: `/products/${product.handle}`
          });
          btn.classList.add('active');
        });
    }
  }

  updateWishlistButtons() {
    document.querySelectorAll('.wishlist-btn').forEach(btn => {
      if (this.isInWishlist(btn.dataset.productHandle)) {
        btn.classList.add('active');
      }
    });
  }

  /* ---------- helpers ---------- */

  productUrl(handle) {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return `${root}products/${handle}.js`;
  }

  // Same variant the product card uses: first available, else first
  getPriceData(product) {
    const variants = product.variants || [];
    const variant = variants.find(v => v.available) || variants[0] || {};
    return {
      variantId: variant.id || '',
      price: variant.price != null ? variant.price : product.price,
      compare: variant.compare_at_price || 0
    };
  }

  getConfig() {
    const root = document.getElementById('wishlist-page');
    const d = root ? root.dataset : {};
    return {
      isDealer: d.isDealer === 'true',
      currency:
        d.currency ||
        (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
        'INR'
    };
  }

  formatMoney(cents, currency) {
    const amount = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: currency || 'INR',
        minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
        maximumFractionDigits: 2
      }).format(amount);
    } catch (e) {
      return 'Rs ' + amount;
    }
  }

  escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* ---------- dealer price (follows whatever Clay shows on the real product cards) ---------- */

  getStoredRatio() {
    try {
      return JSON.parse(localStorage.getItem(this.ratioKey)) || null;
    } catch (e) {
      return null;
    }
  }

  // Read the number the shopper actually sees in a product card price (Clay already applied)
  readDisplayedPrice(card) {
    // Clay renders: .clay-wholesale-price > .price (wholesale) + .compare-at-price (original)
    const priceEl =
      card.querySelector('.clay-wholesale-price .price') ||
      card.querySelector('.sg-price .price') ||
      card.querySelector('.price');

    let text = '';
    if (priceEl) {
      text = priceEl.textContent || '';
    } else {
      // last resort: ignore <style>/<script>/<template> text, which contains stray numbers
      const clone = card.cloneNode(true);
      clone
        .querySelectorAll('style, script, template, s, del, strike, .compare-at-price, .visually-hidden')
        .forEach(n => n.remove());
      text = clone.textContent || '';
    }

    const m = text.replace(/,/g, '').match(/\d+(?:\.\d+)?/);
    return m ? parseFloat(m[0]) : 0;
  }

  readNativeMinor(card) {
    const widget = card.querySelector('[data-sgp-shopify-price-minor]');
    if (widget) return Number(widget.getAttribute('data-sgp-shopify-price-minor')) || 0;

    const tpl = card.querySelector('template[data-sgp-native-template]');
    if (tpl) {
      const m = (tpl.content.textContent || '').replace(/,/g, '').match(/\d+(?:\.\d+)?/);
      if (m) return Math.round(parseFloat(m[0]) * 100);
    }
    return 0;
  }

  // Compare Clay's displayed price vs the native Shopify price.
  // Clay writes <div class="sg-price ... clay-wholesale-price"> inside
  // <product-price data-sgp-shopify-price-minor="1000000">. That class only exists for dealers,
  // so a normal user can never produce a ratio.
  learnFromDocument(doc) {
    const ratios = [];
    const variants = {};

    doc.querySelectorAll('.clay-wholesale-price').forEach(el => {
      const widget = el.closest('[data-sgp-shopify-price-minor]');
      const native = widget ? Number(widget.getAttribute('data-sgp-shopify-price-minor')) || 0 : 0;
      const shown = this.readDisplayedPrice(el);
      if (!native || !shown) return;

      const ratio = (shown * 100) / native;
      if (!(ratio > 0.01 && ratio < 0.9999)) return; // not discounted

      const r = Math.round(ratio * 10000) / 10000;
      ratios.push(r);

      const vid = el.getAttribute('data-variant-id') || (widget && widget.getAttribute('data-variant-id'));
      if (vid) variants[vid] = r;
    });

    if (!ratios.length) return null;

    ratios.sort((a, b) => a - b);
    return { global: ratios[Math.floor(ratios.length / 2)], variants, ts: Date.now() };
  }

  // Save the learned ratio (if the rule changed, forget old per-variant values)
  storeRatio(result) {
    if (!result) return;
    const prev = this.getStoredRatio();
    if (prev && Math.abs((prev.global || 0) - result.global) < 0.0005) {
      result.variants = Object.assign({}, prev.variants || {}, result.variants);
    }
    try {
      localStorage.setItem(this.ratioKey, JSON.stringify(result));
    } catch (e) { /* ignore */ }
  }

  // On every normal page: once Clay has rendered dealer prices on any product card,
  // remember the ratio so the wishlist can use it.
  startPassiveLearning() {
    if (window.location.pathname.includes('/pages/wishlist')) return;

    const run = () => this.storeRatio(this.learnFromDocument(document));

    [800, 2000, 4500, 9000].forEach(ms => setTimeout(run, ms));

    // Also catch cards that appear later (filters, infinite scroll, tabs) for a while
    let timer = null;
    const observer = new MutationObserver(() => {
      clearTimeout(timer);
      timer = setTimeout(run, 600);
    });
    observer.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => observer.disconnect(), 30000);
  }

  // Loads the real collection page invisibly so Clay runs on it, then reads its prices
  probeClayRatio() {
    return new Promise(resolve => {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const iframe = document.createElement('iframe');
      iframe.setAttribute('aria-hidden', 'true');
      iframe.tabIndex = -1;
      iframe.style.cssText =
        'position:fixed;left:-10000px;top:0;width:1280px;height:900px;border:0;opacity:0;pointer-events:none;';
      iframe.src = `${root}collections/all?sg_probe=1`;

      let done = false;
      let timer = null;
      let tries = 0;

      const finish = (result) => {
        if (done) return;
        done = true;
        clearInterval(timer);
        iframe.remove();
        resolve(result);
      };

      iframe.addEventListener('load', () => {
        timer = setInterval(() => {
          tries++;
          let result = null;
          try {
            result = this.learnFromDocument(iframe.contentDocument);
          } catch (e) {
            finish(null);
            return;
          }
          if (result || tries >= 14) finish(result);
        }, 500);
      });

      document.body.appendChild(iframe);
      setTimeout(() => finish(null), 15000);
    });
  }

  getDealerRatio(variantId) {
    const stored = this.dealerRatio;
    if (!stored) return null;
    if (variantId && stored.variants && stored.variants[variantId]) return stored.variants[variantId];
    return stored.global || null;
  }

  /* ---------- price painting ---------- */

  paintItem(handle) {
    const grid = document.getElementById('wishlist-grid');
    if (!grid) return;
    const el = grid.querySelector(`.wishlist-item[data-handle="${CSS.escape(handle)}"] .wishlist-item-price`);
    if (!el) return;

    const cfg = this.getConfig();
    const saved = this.getWishlist().find(i => i.handle === handle) || {};
    const data = this.live[handle] || {
      variantId: saved.variantId || '',
      price: saved.price,
      compare: saved.compare_price || 0
    };

    const price = Number(data.price) || 0;
    const compare = Number(data.compare) || 0;

    if (cfg.isDealer) {
      // Dealer: ONLY the discounted price
      const ratio = this.getDealerRatio(data.variantId);
      if (ratio) {
        el.innerHTML = `<span class="wishlist-price-now">${this.formatMoney(Math.round(price * ratio), cfg.currency)}</span>`;
      } else if (this.probeFinished) {
        // Could not learn a wholesale price: show the normal price rather than nothing
        el.innerHTML = `<span class="wishlist-price-now">${this.formatMoney(price, cfg.currency)}</span>`;
      } else {
        el.innerHTML = '<span class="wishlist-price-loading">Loading price…</span>';
      }
      return;
    }

    // Normal user: price + compare-at price
    let html = `<span class="wishlist-price-now">${this.formatMoney(price, cfg.currency)}</span>`;
    if (compare > price) {
      html += `<span class="wishlist-price-was">${this.formatMoney(compare, cfg.currency)}</span>`;
    }
    el.innerHTML = html;
  }

  paintAll() {
    this.getWishlist().forEach(item => this.paintItem(item.handle));
  }

  /* ---------- wishlist page ---------- */

  loadWishlistPage() {
    const wishlist = this.getWishlist();
    const emptyDiv = document.getElementById('wishlist-empty');
    const gridDiv = document.getElementById('wishlist-grid');
    const clearBtn = document.getElementById('clear-wishlist');

    if (!gridDiv) return;

    if (wishlist.length === 0) {
      emptyDiv.style.display = 'block';
      gridDiv.style.display = 'none';
      if (clearBtn) clearBtn.style.display = 'none';
      return;
    }

    emptyDiv.style.display = 'none';
    gridDiv.style.display = 'grid';
    if (clearBtn) clearBtn.style.display = 'block';

    const cfg = this.getConfig();
    const token = ++this.renderToken;
    this.live = {};
    this.dealerRatio = cfg.isDealer ? this.getStoredRatio() : null;
    this.probeFinished = !cfg.isDealer;

    gridDiv.innerHTML = wishlist.map(item => `
      <div class="wishlist-item" data-handle="${this.escapeHtml(item.handle)}">
        <button class="wishlist-remove-btn" data-handle="${this.escapeHtml(item.handle)}" aria-label="Remove">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
        <a href="${this.escapeHtml(item.url)}">
          <img src="${this.escapeHtml(item.image)}" alt="${this.escapeHtml(item.title)}" class="wishlist-item-image">
          <h3 class="wishlist-item-title">${this.escapeHtml(item.title)}</h3>
          <div class="wishlist-item-price"></div>
        </a>
      </div>
    `).join('');

    // 1) Instant paint from the saved snapshot (+ last known dealer ratio)
    this.paintAll();

    // 2) Refresh with LIVE Shopify prices
    wishlist.forEach(item => {
      fetch(this.productUrl(item.handle))
        .then(res => {
          if (!res.ok) throw new Error('not found');
          return res.json();
        })
        .then(product => {
          if (token !== this.renderToken) return;
          const info = this.getPriceData(product);
          this.live[item.handle] = info;
          this.paintItem(item.handle);

          const list = this.getWishlist();
          const saved = list.find(i => i.handle === item.handle);
          if (saved) {
            saved.price = info.price;
            saved.compare_price = info.compare;
            saved.variantId = info.variantId;
            localStorage.setItem(this.storageKey, JSON.stringify(list));
          }
        })
        .catch(() => {});
    });

    // 3) Dealer: learn the CURRENT Clay price from the real product cards
    if (cfg.isDealer) {
      this.probeClayRatio().then(result => {
        if (token !== this.renderToken) return;
        this.probeFinished = true;
        if (result) this.storeRatio(result);
        this.dealerRatio = this.getStoredRatio();
        this.paintAll();
      });
    }
  }
}

// Initialize wishlist
document.addEventListener('DOMContentLoaded', () => {
  window.wishlistApp = new Wishlist();
});